import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { RevealText, Reveal } from "@/components/site/Reveal";
import { BrandMarquee } from "@/components/site/Marquee";
import { IMAGES, SERVICES, STATS, INDUSTRIES } from "@/lib/assets";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.45, 0.85]);

  return (
    <section ref={ref} data-testid="hero" className="relative h-screen w-full overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Container vessel at sea" className="h-full w-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-black" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/40" />

      <div className="relative z-10 h-full mx-auto max-w-[1600px] px-5 sm:px-8 flex flex-col justify-end pb-16">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-6"
        >
          Global Transport & Logistics
        </motion.p>
        <h1 className="font-display uppercase tracking-tighter leading-[0.85] text-[15vw] sm:text-[12vw] lg:text-[9.5vw]">
          <RevealText lines={["Your business,", "our"]} testid="hero-title" />
          <span className="inline-flex items-baseline gap-4">
            <RevealText lines={["priority"]} delay={0.36} className="text-primary" />
          </span>
        </h1>
        <div className="mt-10 flex flex-col sm:flex-row sm:items-end justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }}
            className="max-w-md text-white/70 text-lg"
          >
            Smarter international freight forwarding — powered by technology, driven by service. From origin to destination, we deliver clarity, care, and global reach every step of the way.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }}
            className="flex items-center gap-4"
          >
            <Link to="/contact" data-testid="hero-quote-btn" className="inline-flex items-center gap-2 hover-fill px-7 py-4 font-mono text-xs uppercase tracking-widest">
              Get a Quote <ArrowUpRight size={16} />
            </Link>
            <Link to="/services" className="inline-flex items-center gap-2 border border-white/20 px-7 py-4 font-mono text-xs uppercase tracking-widest hover:border-primary hover:text-primary transition-colors duration-300">
              Our Services
            </Link>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}
        className="absolute bottom-8 right-8 z-10 hidden sm:flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-white/50"
      >
        Scroll <ArrowDown size={14} className="animate-bounce" />
      </motion.div>
    </section>
  );
};

const StatsBar = () => (
  <section data-testid="stats" className="border-b border-white/10 bg-[#050505]">
    <div className="mx-auto max-w-[1600px] px-5 sm:px-8 grid grid-cols-2 lg:grid-cols-4">
      {STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.1} className={`py-12 px-2 border-white/10 ${i % 2 === 0 ? "border-r" : ""} ${i < 2 ? "border-b lg:border-b-0" : ""} ${i !== 3 ? "lg:border-r" : ""}`}>
          <p className="font-display text-5xl sm:text-6xl text-primary tracking-tighter">{s.value}</p>
          <p className="font-mono text-[11px] uppercase tracking-widest text-white/50 mt-3">{s.label}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const ServicesPreview = () => (
  <section data-testid="services-preview" className="mx-auto max-w-[1600px] px-5 sm:px-8 py-28">
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">/ Explore our services</p>
        <h2 className="font-display text-4xl sm:text-6xl uppercase tracking-tighter leading-[0.9]">A full suite of<br />freight forwarding.</h2>
      </Reveal>
      <Reveal delay={0.1}>
        <Link to="/services" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-white/70 hover:text-primary transition-colors">
          All services <ArrowUpRight size={14} />
        </Link>
      </Reveal>
    </div>

    <div className="grid md:grid-cols-3 gap-5 auto-rows-[320px]">
      {SERVICES.map((s, i) => (
        <Reveal key={s.key} delay={i * 0.06}>
          <Link to={`/services/${s.key}`} data-testid={`service-card-${s.key}`} className="group relative block h-full w-full overflow-hidden border border-white/10 hover:border-primary/60 transition-colors duration-500">
            <img src={s.img} alt={s.title} className="absolute inset-0 h-full w-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700 ease-out" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="relative z-10 h-full p-7 flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs text-primary">{s.num}</span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/60 border border-white/20 px-2 py-1">{s.tag}</span>
              </div>
              <div>
                <h3 className="font-display text-2xl sm:text-3xl uppercase tracking-tight">{s.title}</h3>
                <p className="text-white/60 text-sm mt-2 max-w-sm">{s.desc}</p>
              </div>
            </div>
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
);

const Manifesto = () => {
  const chapters = [
    { n: "01", t: "More than a forwarder", d: "We don't just move freight — we move with purpose. We align with your business goals and adapt to your industry's demands to simplify your global logistics." },
    { n: "02", t: "Powered by technology", d: "Real-time tracking, on-time delivery and customizable logistics solutions give you clarity and control over every shipment, end to end." },
    { n: "03", t: "A partner in your growth", d: "Join the growing number of companies that rely on Freight IQ for transparent, seamless, and customer-focused freight solutions around the world." },
  ];
  return (
    <section data-testid="manifesto" className="relative border-y border-white/10 bg-[#080808]">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 py-28 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-4">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">/ Why Freight IQ</p>
            <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tighter leading-[0.9] sticky top-28">
              A partner in<br />your growth.
            </h2>
          </Reveal>
        </div>
        <div className="lg:col-span-8 divide-y divide-white/10">
          {chapters.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.08} className="py-10 grid grid-cols-12 gap-6 items-start">
              <span className="col-span-12 sm:col-span-2 font-display text-5xl stroke-text">{c.n}</span>
              <div className="col-span-12 sm:col-span-10">
                <h3 className="font-display text-2xl sm:text-3xl uppercase tracking-tight">{c.t}</h3>
                <p className="text-white/60 mt-3 max-w-xl text-lg">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Industries = () => (
  <section data-testid="industries" className="mx-auto max-w-[1600px] px-5 sm:px-8 py-28">
    <Reveal>
      <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">/ Industries we serve</p>
      <h2 className="font-display text-4xl sm:text-6xl uppercase tracking-tighter leading-[0.9] max-w-3xl">Customized solutions for every sector.</h2>
      <p className="text-white/60 mt-6 max-w-2xl text-lg">Each industry brings its own logistics challenges. With deep expertise and a global network, we help businesses move smarter — no matter the cargo.</p>
    </Reveal>
    <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
      {INDUSTRIES.map((ind, i) => (
        <Reveal key={ind} delay={(i % 4) * 0.06} className="bg-[#050505] group">
          <div className="p-8 h-full hover:bg-primary transition-colors duration-300">
            <span className="font-mono text-xs text-primary group-hover:text-white transition-colors">{String(i + 1).padStart(2, "0")}</span>
            <p className="font-display text-xl uppercase tracking-tight mt-4 leading-tight group-hover:text-white transition-colors">{ind}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const CTA = () => (
  <section data-testid="home-cta" className="relative h-[70vh] overflow-hidden">
    <img src={IMAGES.portCrane} alt="Port container terminal" className="absolute inset-0 h-full w-full object-cover opacity-60" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/50 to-[#050505]/60" />
    <div className="relative z-10 h-full mx-auto max-w-[1600px] px-5 sm:px-8 flex flex-col justify-center">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-widest text-primary mb-5">/ Ready to start your project?</p>
        <h2 className="font-display text-5xl sm:text-7xl lg:text-8xl uppercase tracking-tighter leading-[0.85] max-w-4xl">
          Delivering value through <span className="text-primary">trusted</span> relationships.
        </h2>
        <a href="mailto:sales@freight-iq.com?subject=New%20Enquiry%20-%20Freight%20IQ" data-testid="cta-quote-btn" className="inline-flex items-center gap-3 mt-10 hover-fill px-8 py-4 font-mono text-xs uppercase tracking-widest w-fit">
          Connect with us <ArrowUpRight size={16} />
        </a>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <BrandMarquee />
      <StatsBar />
      <ServicesPreview />
      <Manifesto />
      <Industries />
      <CTA />
    </div>
  );
}
